/**
 * PlanSetupPage — unified plan & add-on management
 *
 * Consolidates:
 *   - /plans          → Plans tab  (PlanCardGrid / FeatureMatrix + PlanSheet slide-over)
 *   - /settings/add-ons → Add-ons tab (catalog table + create/edit modal)
 *   - /settings/helcim-plans → removed; sync is now automatic on create/update
 *
 * Plans and add-ons are created/updated in DB and Helcim atomically by the backend.
 * No manual sync buttons are exposed here.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { cn, Button, useToast } from '@respectly/ui';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Label,
  Textarea,
  Switch,
  Checkbox,
} from '@respectly/ui';
import {
  Layers,
  Plus,
  Users,
  LayoutGrid,
  TableProperties,
  Package,
  Search,
  CheckCircle2,
  Pencil,
  Trash2,
  ChevronDown,
  X,
  Loader2,
  Zap,
  Tag,
} from 'lucide-react';
import { AiCreditCostsTab } from '../settings/AiCreditCosts';
import { usePlansStore } from '../../stores/usePlansStore';
import { useAddOnsStore, type AddOnListItem, type SimplePlanItem } from '../../stores/useAddOnsStore';
import { useDiscountsStore } from '../../stores/useDiscountsStore';
import { DiscountTable, DiscountSheet } from '../../components/discounts';
import {
  PlanCardGrid,
  PlanCardGridSkeleton,
  FeatureMatrix,
  FeatureMatrixSkeleton,
  SubscribersList,
  PlanSheet,
} from '../../components/plans';
import type { SuperAdminPlan, CreatePlanDto, CreateDiscountDto, DiscountWithStats } from '@respectly/shared-types';

// ─── Local Types ──────────────────────────────────────────────────────────────

type ActiveTab = 'plans' | 'addons' | 'ai-credits' | 'discounts';
type ViewMode = 'cards' | 'matrix';

interface AddOnFormState {
  name: string;
  description: string;
  category: 'limit-increase' | 'phone-number';
  billingType: 'one_time' | 'recurring';
  unitPrice: string;
  minQuantity: string;
  affectedLimitField: string;
  planIds: string[];
  isActive: boolean;
}

const EMPTY_ADDON_FORM: AddOnFormState = {
  name: '',
  description: '',
  category: 'limit-increase',
  billingType: 'one_time',
  unitPrice: '',
  minQuantity: '1',
  affectedLimitField: '',
  planIds: [],
  isActive: true,
};

const LIMIT_FIELD_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'aiCreditsPerMonth', label: 'AI Credits' },
  { value: 'smsCreditsPerMonth', label: 'SMS Credits' },
  { value: 'emailCreditsPerMonth', label: 'Email Credits' },
  { value: 'maxContacts', label: 'Max Contacts' },
  { value: 'maxUsers', label: 'Max Users' },
];

// Map credit-type strings (legacy `usage` add-ons) onto the limit-field enum
const CREDIT_TYPE_TO_LIMIT_FIELD: Record<string, string> = {
  ai_credits: 'aiCreditsPerMonth',
  sms: 'smsCreditsPerMonth',
  email: 'emailCreditsPerMonth',
};

// ─── Category Badge ───────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<string, { label: string; cls: string }> = {
  'limit-increase': {
    label: 'Limit Boost',
    cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800',
  },
  'phone-number': {
    label: 'Phone Number',
    cls: 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/30 dark:text-cyan-400 dark:border-cyan-800',
  },
  // Legacy values (still in DB) — rendered for row badges but not selectable in the dialog
  usage: {
    label: 'Usage',
    cls: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800',
  },
  seat: {
    label: 'Seat',
    cls: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-400 dark:border-violet-800',
  },
  feature: {
    label: 'Feature',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800',
  },
};

function CategoryBadge({ category }: { category: string }) {
  const cfg = CATEGORY_CONFIG[category] ?? {
    label: category,
    cls: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        cfg.cls
      )}
    >
      {cfg.label}
    </span>
  );
}

// ─── Add-on Table Row ─────────────────────────────────────────────────────────

function AddOnRow({
  addOn,
  plans,
  onEdit,
  onDeactivate,
}: {
  addOn: AddOnListItem;
  plans: SimplePlanItem[];
  onEdit: (addOn: AddOnListItem) => void;
  onDeactivate: (id: string) => void;
}) {
  return (
    <tr className="group border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
      {/* Name / Key */}
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-slate-900 dark:text-white leading-tight">
          {addOn.name}
        </p>
        <p className="text-xs text-slate-400 dark:text-slate-500 font-mono mt-0.5">{addOn.key}</p>
      </td>

      {/* Category */}
      <td className="px-4 py-3">
        <CategoryBadge category={addOn.category} />
      </td>

      {/* Billing Type */}
      <td className="px-4 py-3">
        <span
          className={cn(
            'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
            addOn.billingType === 'recurring'
              ? 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-400 dark:border-indigo-800'
              : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
          )}
        >
          {addOn.billingType === 'recurring' ? 'Recurring' : 'One-time'}
        </span>
      </td>

      {/* Price */}
      <td className="px-4 py-3 text-sm tabular-nums text-slate-700 dark:text-slate-300">
        ${Number(addOn.unitPrice).toFixed(2)}
      </td>

      {/* Plans */}
      <td className="px-4 py-3">
        {addOn.assignedPlanIds && addOn.assignedPlanIds.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {addOn.assignedPlanIds.map((pid) => {
              const plan = plans.find((p) => p.id === pid);
              return plan ? (
                <span
                  key={pid}
                  className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800 px-2 py-0.5 text-xs font-medium"
                >
                  {plan.name}
                </span>
              ) : null;
            })}
          </div>
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500">None</span>
        )}
      </td>

      {/* Helcim */}
      <td className="px-4 py-3">
        {addOn.billingType === 'one_time' ? (
          <span className="text-xs text-slate-400 dark:text-slate-500">N/A</span>
        ) : addOn.isSynced ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            Synced
          </span>
        ) : (
          <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Pending</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span
          className={cn(
            'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
            addOn.isActive
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800'
              : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700'
          )}
        >
          {addOn.isActive ? 'Active' : 'Inactive'}
        </span>
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(addOn)}
            className="p-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-200 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-700 transition-colors"
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          {addOn.isActive && (
            <button
              onClick={() => onDeactivate(addOn.id)}
              className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 dark:text-slate-500 dark:hover:text-red-400 dark:hover:bg-red-950/30 transition-colors"
              title="Deactivate"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Add-on Create / Edit Modal ───────────────────────────────────────────────

interface AddOnModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editAddon?: AddOnListItem | null;
  onSave: (form: AddOnFormState, editId?: string) => Promise<boolean>;
  isLoading: boolean;
  plans: SimplePlanItem[];
}

function AddOnModal({
  open,
  onOpenChange,
  editAddon,
  onSave,
  isLoading,
  plans,
}: AddOnModalProps) {
  const [form, setForm] = useState<AddOnFormState>(EMPTY_ADDON_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const updateField = <K extends keyof AddOnFormState>(key: K, value: AddOnFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Reset form when modal opens
  useEffect(() => {
    if (!open) return;
    setFormError(null);
    if (editAddon) {
      // Map legacy categories (`usage`, `seat`, `feature`) onto the 2 simplified
      // categories so the dialog always shows one of the new options.
      let mappedCategory: 'limit-increase' | 'phone-number';
      let mappedLimitField = editAddon.affectedLimitField ?? '';

      if (editAddon.category === 'phone-number' || editAddon.category === 'feature') {
        mappedCategory = 'phone-number';
      } else {
        mappedCategory = 'limit-increase';
        if (editAddon.category === 'seat' && !mappedLimitField) {
          mappedLimitField = 'maxUsers';
        } else if (editAddon.category === 'usage' && !mappedLimitField && editAddon.creditType) {
          mappedLimitField = CREDIT_TYPE_TO_LIMIT_FIELD[editAddon.creditType] ?? '';
        }
      }

      setForm({
        name: editAddon.name,
        description: editAddon.description ?? '',
        category: mappedCategory,
        billingType: editAddon.billingType,
        unitPrice: String(editAddon.unitPrice),
        minQuantity: String(editAddon.minQuantity ?? 1),
        affectedLimitField: mappedLimitField,
        planIds: editAddon.assignedPlanIds ?? [],
        isActive: editAddon.isActive,
      });
    } else {
      setForm(EMPTY_ADDON_FORM);
    }
  }, [open, editAddon, plans]);

  const isEditing = !!editAddon;
  const isLimitBoost = form.category === 'limit-increase';

  const selectablePlans = plans.filter((p) => p.tierLevel !== 0);
  const allSelected =
    selectablePlans.length > 0 && form.planIds.length === selectablePlans.length;
  const masterChecked: boolean | 'indeterminate' = allSelected
    ? true
    : form.planIds.length === 0
      ? false
      : 'indeterminate';

  const togglePlan = (planId: string, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      planIds: checked
        ? [...prev.planIds, planId]
        : prev.planIds.filter((id) => id !== planId),
    }));
  };

  const toggleAllPlans = (checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      planIds: checked ? selectablePlans.map((p) => p.id) : [],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (!form.unitPrice || isNaN(parseFloat(form.unitPrice))) {
      setFormError('A valid unit price is required.');
      return;
    }
    if (isLimitBoost && !form.affectedLimitField) {
      setFormError('Select which limit this add-on boosts.');
      return;
    }
    if (form.planIds.length === 0) {
      setFormError('Select at least one plan.');
      return;
    }
    setFormError(null);
    const success = await onSave(form, editAddon?.id);
    if (!success) {
      setFormError('Failed to save add-on. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden p-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700">
          <DialogTitle className="text-base font-semibold text-slate-900 dark:text-white">
            {isEditing ? `Edit: ${editAddon?.name}` : 'New Add-on'}
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {formError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-4 py-3">
              <p className="text-sm text-red-700 dark:text-red-400">{formError}</p>
            </div>
          )}

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="ao-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="ao-name"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g., Extra SMS Credits"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="ao-desc">Description</Label>
            <Textarea
              id="ao-desc"
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="What does this add-on provide?"
              rows={2}
            />
          </div>

          {/* Category + Billing Type */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.category}
                onValueChange={(v) =>
                  updateField('category', v as 'limit-increase' | 'phone-number')
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="limit-increase">Limit Boost</SelectItem>
                  <SelectItem value="phone-number">Phone Number</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>
                Billing Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.billingType}
                onValueChange={(v) => updateField('billingType', v as 'one_time' | 'recurring')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One-time</SelectItem>
                  <SelectItem value="recurring">Recurring</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Price + Min Quantity */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ao-price">
                Unit Price (USD) <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                  $
                </span>
                <Input
                  id="ao-price"
                  type="number"
                  min="0"
                  step="0.01"
                  className="pl-7"
                  value={form.unitPrice}
                  onChange={(e) => updateField('unitPrice', e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ao-qty">Minimum Quantity</Label>
              <Input
                id="ao-qty"
                type="number"
                min="1"
                value={form.minQuantity}
                onChange={(e) => updateField('minQuantity', e.target.value)}
              />
            </div>
          </div>

          {/* Plan Assignment */}
          <div className="space-y-1.5">
            <Label>
              Plans <span className="text-red-500">*</span>
            </Label>
            {selectablePlans.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No eligible plans available.
              </p>
            ) : (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                <Checkbox
                  id="ao-plans-all"
                  label="Select all plans"
                  checked={masterChecked}
                  onCheckedChange={(c) => toggleAllPlans(c === true)}
                />
                <div className="border-t border-slate-200 dark:border-slate-700" />
                {selectablePlans.map((p) => (
                  <Checkbox
                    key={p.id}
                    id={`ao-plan-${p.id}`}
                    label={p.name}
                    checked={form.planIds.includes(p.id)}
                    onCheckedChange={(c) => togglePlan(p.id, c === true)}
                  />
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Select which plans expose this add-on. The Free plan is excluded.
            </p>
          </div>

          {/* Limit field — only for Limit Boost */}
          {isLimitBoost && (
            <div className="space-y-1.5">
              <Label htmlFor="ao-lf">
                Associated Limit <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.affectedLimitField || '__none'}
                onValueChange={(v) =>
                  updateField('affectedLimitField', v === '__none' ? '' : v)
                }
              >
                <SelectTrigger id="ao-lf">
                  <SelectValue placeholder="Select a limit" />
                </SelectTrigger>
                <SelectContent>
                  {LIMIT_FIELD_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Each unit purchased adds 1 to this limit.
              </p>
            </div>
          )}

          {/* Active Toggle */}
          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-white">Active</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Inactive add-ons are hidden from customers
              </p>
            </div>
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => updateField('isActive', v)}
            />
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Saving…
              </>
            ) : isEditing ? (
              'Save Changes'
            ) : (
              'Create Add-on'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── PlanSetupPage ────────────────────────────────────────────────────────────

export function PlanSetupPage() {
  const navigate = useNavigate();

  // Read initial tab from URL search param
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      const tab = p.get('tab');
      if (tab === 'addons') return 'addons';
      if (tab === 'ai-credits') return 'ai-credits';
    }
    return 'plans';
  });

  const { toast } = useToast();

  // ── Plans tab state ──────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [showInactive, setShowInactive] = useState(true);
  const [planSheetOpen, setPlanSheetOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SuperAdminPlan | undefined>(undefined);
  const [subscribersPlan, setSubscribersPlan] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // ── Add-ons tab state ────────────────────────────────────────────────────
  const [addOnSearch, setAddOnSearch] = useState('');
  const [addOnCategoryFilter, setAddOnCategoryFilter] = useState('');
  const [addOnBillingFilter, setAddOnBillingFilter] = useState('');
  const [addOnModalOpen, setAddOnModalOpen] = useState(false);
  const [editingAddOn, setEditingAddOn] = useState<AddOnListItem | null>(null);

  // ── Discounts tab state ──────────────────────────────────────────────────
  const [discountSheetOpen, setDiscountSheetOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<DiscountWithStats | undefined>(undefined);

  // ── Plans store ──────────────────────────────────────────────────────────
  const {
    plans,
    isLoadingList: plansLoading,
    loadPlans,
    subscribers,
    subscribersPagination,
    isLoadingSubscribers,
    loadSubscribers,
    createPlan,
    updatePlan,
    isPerformingAction: planSaving,
    actionError: planActionError,
  } = usePlansStore();

  // ── Add-ons store ────────────────────────────────────────────────────────
  const {
    addOns,
    plans: addonPlans,
    isLoadingList: addOnsLoading,
    isPerformingAction: addOnSaving,
    pagination: addOnPagination,
    loadAddOns,
    loadMoreAddOns,
    loadPlans: loadAddonPlans,
    setFilter: setAddOnFilter,
    setSearchTerm,
    clearFilters: clearAddOnFilters,
    createAddOn,
    updateAddOn,
    updatePlanAssignments,
    deactivateAddOn,
  } = useAddOnsStore();

  // ── Discounts store ──────────────────────────────────────────────────────
  const {
    discounts,
    isLoadingList: discountsLoading,
    isPerformingAction: discountSaving,
    actionError: discountActionError,
    loadDiscounts,
    createDiscount,
    updateDiscount,
    deleteDiscount,
  } = useDiscountsStore();

  // ── Bootstrap ────────────────────────────────────────────────────────────
  useEffect(() => {
    loadPlans(1, true);
    loadAddOns();
    loadAddonPlans();
  }, [loadPlans, loadAddOns, loadAddonPlans]);

  useEffect(() => {
    if (activeTab === 'discounts') loadDiscounts();
  }, [activeTab, loadDiscounts]);

  // Sync tab to URL
  useEffect(() => {
    const url = new URL(window.location.href);
    if (activeTab !== 'plans') {
      url.searchParams.set('tab', activeTab);
    } else {
      url.searchParams.delete('tab');
    }
    window.history.replaceState(null, '', url.toString());
  }, [activeTab]);

  // Load subscribers when sheet opens
  useEffect(() => {
    if (subscribersPlan) loadSubscribers(subscribersPlan.id, 1);
  }, [subscribersPlan, loadSubscribers]);

  // ── Derived values ───────────────────────────────────────────────────────
  const sortedPlans = [...(showInactive ? plans : plans.filter((p) => p.isActive))].sort(
    (a, b) => a.displayOrder - b.displayOrder
  );
  const activePlansCount = plans.filter((p) => p.isActive).length;
  const activeAddOnsCount = addOns.filter((a) => a.isActive).length;
  const hasMoreAddOns = addOnPagination.skip + addOnPagination.take < addOnPagination.total;

  const tabs = [
    { id: 'plans' as ActiveTab, label: 'Plans', Icon: Layers, count: activePlansCount },
    { id: 'addons' as ActiveTab, label: 'Add-ons', Icon: Package, count: activeAddOnsCount },
    { id: 'ai-credits' as ActiveTab, label: 'AI Credits', Icon: Zap, count: 0 },
    { id: 'discounts' as ActiveTab, label: 'Discounts', Icon: Tag, count: discounts.filter(d => d.isActive).length },
  ];

  // ── Plan handlers ────────────────────────────────────────────────────────
  const handleOpenCreatePlan = () => {
    setEditingPlan(undefined);
    setPlanSheetOpen(true);
  };

  const [isResyncingHelcim, setIsResyncingHelcim] = useState(false);
  const handleResyncAllHelcim = async () => {
    if (!window.confirm(
      'Re-sync every paid plan to Helcim?\n\nThis pushes the current trialDays, name, description, price, and status from our database to Helcim. Use this after changing trialDays on existing plans.'
    )) {
      return;
    }
    setIsResyncingHelcim(true);
    try {
      const api = getApiClient();
      const response = await api.post<{
        count: number;
        results: Array<{ planName: string; ok: boolean; error?: string }>;
      }>(API_ENDPOINTS.SUPER_ADMIN.PLANS.RESYNC_HELCIM_ALL, {});
      const failed = response.results.filter((r) => !r.ok);
      if (failed.length === 0) {
        toast({
          title: 'Helcim sync complete',
          description: `Re-synced ${response.count} plan${response.count === 1 ? '' : 's'} to Helcim.`,
        });
      } else {
        toast({
          title: 'Helcim sync finished with errors',
          description: `${response.count - failed.length}/${response.count} succeeded. Failed: ${failed.map((f) => f.planName).join(', ')}`,
          variant: 'error',
        });
      }
    } catch (err) {
      toast({
        title: 'Helcim sync failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    } finally {
      setIsResyncingHelcim(false);
    }
  };

  const handleOpenEditPlan = (planId: string) => {
    const found = plans.find((p) => p.id === planId);
    if (found) {
      setEditingPlan(found as unknown as SuperAdminPlan);
      setPlanSheetOpen(true);
    }
  };

  const handlePlanSubmit = async (dto: CreatePlanDto): Promise<boolean> => {
    if (editingPlan) {
      const planName = editingPlan.name;
      const ok = await updatePlan(editingPlan.id, dto);
      if (ok) {
        setPlanSheetOpen(false);
        toast({
          title: 'Plan updated',
          description: `${planName} has been updated.`,
        });
      } else {
        toast({
          title: 'Update failed',
          description:
            usePlansStore.getState().actionError?.message ?? 'Unable to update plan.',
          variant: 'error',
        });
      }
      return ok;
    }
    const newId = await createPlan(dto);
    if (newId) {
      setPlanSheetOpen(false);
      toast({
        title: 'Plan created',
        description: `${dto.name} has been created.`,
      });
      return true;
    }
    toast({
      title: 'Create failed',
      description:
        usePlansStore.getState().actionError?.message ?? 'Unable to create plan.',
      variant: 'error',
    });
    return false;
  };

  // ── Add-on handlers ──────────────────────────────────────────────────────
  const handleAddOnSearch = (val: string) => {
    setAddOnSearch(val);
    setSearchTerm(val);
  };

  const handleCategoryFilter = (val: string) => {
    setAddOnCategoryFilter(val);
    setAddOnFilter('category', val || undefined);
  };

  const handleBillingFilter = (val: string) => {
    setAddOnBillingFilter(val);
    setAddOnFilter('billingType', (val || undefined) as 'one_time' | 'recurring' | undefined);
  };

  const handleClearFilters = () => {
    setAddOnSearch('');
    setAddOnCategoryFilter('');
    setAddOnBillingFilter('');
    clearAddOnFilters();
  };

  const handleSaveAddOn = async (form: AddOnFormState, editId?: string): Promise<boolean> => {
    const isLimitBoost = form.category === 'limit-increase';

    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      category: form.category,
      billingType: form.billingType,
      unitPrice: parseFloat(form.unitPrice),
      minQuantity: parseInt(form.minQuantity, 10) || 1,
      isActive: form.isActive,
    };

    if (isLimitBoost) {
      payload.affectedLimitField = form.affectedLimitField;
      payload.increaseAmount = 1;
      payload.increaseType = 'absolute_increase';
    } else {
      payload.affectedLimitField = null;
      payload.increaseAmount = null;
      payload.increaseType = null;
    }

    let ok: boolean;
    if (!editId) {
      payload.planIds = form.planIds;
      ok = await createAddOn(payload);
    } else {
      ok = await updateAddOn(editId, payload);

      if (ok) {
        const editingAddon = addOns.find((a) => a.id === editId);
        const before = new Set(editingAddon?.assignedPlanIds ?? []);
        const after = new Set(form.planIds);
        const changed =
          before.size !== after.size ||
          [...before].some((id) => !after.has(id));
        if (changed) {
          ok = await updatePlanAssignments(
            editId,
            form.planIds.map((planId) => ({ planId, isEnabled: true }))
          );
        }
      }
    }

    if (ok) {
      setAddOnModalOpen(false);
      setEditingAddOn(null);
      toast({
        title: editId ? 'Add-on updated' : 'Add-on created',
        description: `${form.name.trim()} has been ${editId ? 'updated' : 'created'}.`,
      });
    } else {
      toast({
        title: editId ? 'Update failed' : 'Create failed',
        description:
          useAddOnsStore.getState().actionError?.message ??
          (editId ? 'Unable to update add-on.' : 'Unable to create add-on.'),
        variant: 'error',
      });
    }
    return ok;
  };

  // ── Discount handlers ────────────────────────────────────────────────────
  const handleOpenCreateDiscount = () => {
    setEditingDiscount(undefined);
    setDiscountSheetOpen(true);
  };

  const handleOpenEditDiscount = (discount: DiscountWithStats) => {
    setEditingDiscount(discount);
    setDiscountSheetOpen(true);
  };

  const handleDiscountSubmit = async (dto: CreateDiscountDto): Promise<boolean> => {
    if (editingDiscount) {
      const ok = await updateDiscount(editingDiscount.id, dto);
      if (ok) {
        setDiscountSheetOpen(false);
        toast({
          title: 'Discount updated',
          description: `${dto.name} has been updated.`,
        });
      } else {
        toast({
          title: 'Update failed',
          description:
            useDiscountsStore.getState().actionError?.message ?? 'Could not update discount.',
          variant: 'error',
        });
      }
      return ok;
    }

    const newId = await createDiscount(dto);
    if (newId) {
      setDiscountSheetOpen(false);
      toast({
        title: 'Discount created',
        description: `${dto.name} has been created.`,
      });
      return true;
    }
    toast({
      title: 'Create failed',
      description:
        useDiscountsStore.getState().actionError?.message ?? 'Could not create discount.',
      variant: 'error',
    });
    return false;
  };

  const handleDeactivateDiscount = async (id: string) => {
    await updateDiscount(id, { isActive: false });
  };

  const handleDeleteDiscount = async (id: string) => {
    await deleteDiscount(id);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col h-full"
      >
        {/* ── Page Header ─────────────────────────────────────────────── */}
        <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          {/* Title row */}
          <div className="flex items-center justify-between px-4 lg:px-6 pt-4 lg:pt-6 pb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-sm shrink-0">
                <Layers className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">
                  Plan Setup
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Manage subscription plans and add-ons
                </p>
              </div>
            </div>

            {/* Context-aware CTA */}
            <AnimatePresence mode="wait">
              {activeTab === 'plans' && (
                <motion.div
                  key="cta-plans"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.12 }}
                  className="flex items-center gap-2"
                >
                  <Button
                    variant="outline"
                    onClick={handleResyncAllHelcim}
                    disabled={isResyncingHelcim}
                    title="Push current plan settings (trialDays, price, name, description, status) to Helcim. Use this after editing trialDays on existing plans."
                  >
                    {isResyncingHelcim ? 'Re-syncing…' : 'Re-sync Helcim'}
                  </Button>
                  <Button onClick={handleOpenCreatePlan}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Plan
                  </Button>
                </motion.div>
              )}
              {activeTab === 'addons' && (
                <motion.div
                  key="cta-addons"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.12 }}
                >
                  <Button
                    onClick={() => {
                      setEditingAddOn(null);
                      setAddOnModalOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Add-on
                  </Button>
                </motion.div>
              )}
              {activeTab === 'discounts' && (
                <motion.div
                  key="cta-discounts"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.12 }}
                >
                  <Button onClick={handleOpenCreateDiscount}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Discount
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Tab Switcher */}
          <div className="px-4 lg:px-6 pb-4">
            <div
              role="tablist"
              className="flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 w-fit"
            >
              {tabs.map(({ id, label, Icon, count }) => (
                <button
                  key={id}
                  role="tab"
                  aria-selected={activeTab === id}
                  onClick={() => setActiveTab(id)}
                  className={cn(
                    'relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors select-none',
                    activeTab === id
                      ? 'text-slate-900 dark:text-white'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                  )}
                >
                  {activeTab === id && (
                    <motion.div
                      layoutId="plan-setup-tab-pill"
                      className="absolute inset-0 rounded-lg bg-white dark:bg-slate-700 shadow-sm"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                  <Icon className="relative z-10 h-4 w-4 shrink-0" />
                  <span className="relative z-10">{label}</span>
                  {count > 0 && (
                    <span
                      className={cn(
                        'relative z-10 min-w-[1.25rem] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-xs font-semibold',
                        activeTab === id
                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-400'
                          : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Tab Panels ──────────────────────────────────────────────── */}
        <div className="flex-1 overflow-auto">
          <AnimatePresence mode="wait">

            {/* ── Plans Tab ─────────────────────────────── */}
            {activeTab === 'plans' && (
              <motion.div
                key="tab-plans"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
                className="h-full"
              >
                {/* View Toggle */}
                <div className="flex items-center justify-between px-4 lg:px-6 py-3 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-800">
                    {(
                      [
                        { value: 'cards' as ViewMode, label: 'Cards', Icon: LayoutGrid },
                        { value: 'matrix' as ViewMode, label: 'Feature Matrix', Icon: TableProperties },
                      ] as const
                    ).map(({ value, label, Icon }) => (
                      <button
                        key={value}
                        onClick={() => setViewMode(value)}
                        className={cn(
                          'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                          viewMode === value
                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </button>
                    ))}
                  </div>

                  <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={showInactive}
                      onChange={(e) => setShowInactive(e.target.checked)}
                      className="rounded border-slate-300 dark:border-slate-600"
                    />
                    Show inactive plans
                  </label>
                </div>

                <div className="p-4 lg:p-6">
                  {plansLoading ? (
                    viewMode === 'cards' ? (
                      <PlanCardGridSkeleton count={3} />
                    ) : (
                      <FeatureMatrixSkeleton />
                    )
                  ) : sortedPlans.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                      <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                        <Layers className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                      </div>
                      <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1">
                        {showInactive ? 'No plans yet' : 'No active plans'}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mb-5">
                        {showInactive
                          ? 'Create your first subscription plan to start onboarding customers.'
                          : 'All plans are currently inactive. Enable a plan or create a new one.'}
                      </p>
                      <div className="flex items-center gap-2">
                        {!showInactive && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowInactive(true)}
                          >
                            Show inactive
                          </Button>
                        )}
                        <Button onClick={handleOpenCreatePlan}>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Plan
                        </Button>
                      </div>
                    </div>
                  ) : viewMode === 'cards' ? (
                    <PlanCardGrid
                      plans={sortedPlans}
                      onEdit={handleOpenEditPlan}
                      onViewSubscribers={(id) => {
                        const p = plans.find((x) => x.id === id);
                        setSubscribersPlan(p ? { id: p.id, name: p.name } : { id, name: 'Plan' });
                      }}
                    />
                  ) : (
                    <FeatureMatrix plans={sortedPlans} />
                  )}
                </div>
              </motion.div>
            )}

            {/* ── Add-ons Tab ──────────────────────────── */}
            {activeTab === 'addons' && (
              <motion.div
                key="tab-addons"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
              >
                {/* Filter Bar */}
                <div className="flex items-center gap-3 flex-wrap px-4 lg:px-6 py-3 border-b border-slate-100 dark:border-slate-800">
                  {/* Search */}
                  <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={addOnSearch}
                      onChange={(e) => handleAddOnSearch(e.target.value)}
                      placeholder="Search add-ons…"
                      className="w-full pl-9 pr-3 h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:focus:ring-indigo-400"
                    />
                  </div>

                  {/* Category filter */}
                  <select
                    value={addOnCategoryFilter}
                    onChange={(e) => handleCategoryFilter(e.target.value)}
                    className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  >
                    <option value="">All Categories</option>
                    <option value="usage">Usage</option>
                    <option value="seat">Seat</option>
                    <option value="limit-increase">Limit Boost</option>
                    <option value="feature">Feature</option>
                    <option value="phone-number">Phone Number</option>
                  </select>

                  {/* Billing type filter */}
                  <select
                    value={addOnBillingFilter}
                    onChange={(e) => handleBillingFilter(e.target.value)}
                    className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  >
                    <option value="">All Billing Types</option>
                    <option value="one_time">One-time</option>
                    <option value="recurring">Recurring</option>
                  </select>

                  {/* Clear button */}
                  {(addOnSearch || addOnCategoryFilter || addOnBillingFilter) && (
                    <button
                      onClick={handleClearFilters}
                      className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                      Clear
                    </button>
                  )}
                </div>

                {/* Table */}
                <div className="p-4 lg:p-6">
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 shadow-sm">
                    {addOnsLoading && addOns.length === 0 ? (
                      <div className="flex items-center justify-center py-16">
                        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                      </div>
                    ) : addOns.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="h-12 w-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                          <Package className="h-6 w-6 text-slate-400 dark:text-slate-500" />
                        </div>
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                          No add-ons found
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 max-w-xs">
                          Create add-ons to offer optional extras to your customers.
                        </p>
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditingAddOn(null);
                            setAddOnModalOpen(true);
                          }}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1.5" />
                          New Add-on
                        </Button>
                      </div>
                    ) : (
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
                            {['Name / Key', 'Category', 'Billing', 'Price', 'Plans', 'Helcim', 'Status', ''].map(
                              (col) => (
                                <th
                                  key={col}
                                  className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap"
                                >
                                  {col}
                                </th>
                              )
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {addOns.map((a) => (
                            <AddOnRow
                              key={a.id}
                              addOn={a}
                              plans={addonPlans}
                              onEdit={(addon) => {
                                setEditingAddOn(addon);
                                setAddOnModalOpen(true);
                              }}
                              onDeactivate={(id) => deactivateAddOn(id)}
                            />
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Load More */}
                  {hasMoreAddOns && (
                    <div className="mt-4 flex justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadMoreAddOns()}
                        disabled={addOnsLoading}
                      >
                        {addOnsLoading ? (
                          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        ) : (
                          <ChevronDown className="h-4 w-4 mr-1.5" />
                        )}
                        Load More
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── AI Credits Tab ───────────────────────── */}
            {activeTab === 'ai-credits' && (
              <motion.div
                key="tab-ai-credits"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
              >
                <AiCreditCostsTab />
              </motion.div>
            )}

            {/* ── Discounts Tab ────────────────────────── */}
            {activeTab === 'discounts' && (
              <motion.div
                key="tab-discounts"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
              >
                <div className="p-4 lg:p-6">
                  {discountsLoading && discounts.length === 0 ? (
                    <div className="flex items-center justify-center py-24">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                  ) : (
                    <DiscountTable
                      discounts={discounts}
                      onEdit={handleOpenEditDiscount}
                      onDeactivate={handleDeactivateDiscount}
                      onDelete={handleDeleteDiscount}
                    />
                  )}
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>

      {/* ── Plan Slide-over ──────────────────────────────────────────── */}
      <PlanSheet
        open={planSheetOpen}
        onOpenChange={(open) => {
          setPlanSheetOpen(open);
          if (!open) setEditingPlan(undefined);
        }}
        plan={editingPlan}
        onSubmit={handlePlanSubmit}
        isLoading={planSaving}
        actionError={planActionError}
      />

      {/* ── Add-on Modal ─────────────────────────────────────────────── */}
      <AddOnModal
        open={addOnModalOpen}
        onOpenChange={(open) => {
          setAddOnModalOpen(open);
          if (!open) setEditingAddOn(null);
        }}
        editAddon={editingAddOn}
        onSave={handleSaveAddOn}
        isLoading={addOnSaving}
        plans={addonPlans}
      />

      {/* ── Discount Sheet ───────────────────────────────────────────── */}
      <DiscountSheet
        open={discountSheetOpen}
        onOpenChange={(open) => {
          setDiscountSheetOpen(open);
          if (!open) setEditingDiscount(undefined);
        }}
        discount={editingDiscount}
        plans={plans}
        onSubmit={handleDiscountSubmit}
        isLoading={discountSaving}
        actionError={discountActionError}
      />

      {/* ── Subscribers Sheet ────────────────────────────────────────── */}
      <Sheet
        open={!!subscribersPlan}
        onOpenChange={(open) => {
          if (!open) setSubscribersPlan(null);
        }}
      >
        <SheetContent side="right" className="w-[520px] sm:w-[600px] p-0 flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-950/40 shrink-0">
                <Users className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <SheetTitle className="text-base font-semibold">
                  {subscribersPlan?.name ?? 'Plan'} Subscribers
                </SheetTitle>
                <SheetDescription className="text-xs text-slate-500 dark:text-slate-400">
                  Accounts currently on this plan
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-auto px-6 py-4">
            {subscribersPlan && (
              <SubscribersList
                planId={subscribersPlan.id}
                planName={subscribersPlan.name}
                subscribers={subscribers}
                pagination={subscribersPagination}
                isLoading={isLoadingSubscribers}
                onViewAccount={(id) =>
                  navigate({ to: '/accounts/$id', params: { id } })
                }
                onPageChange={(page) => loadSubscribers(subscribersPlan.id, page)}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
